import React, { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Clock } from 'lucide-react';

type TimeFieldProps = {
  name: string;
  rules?: unknown;
  placeholder?: string;
};

/** This component depends on a react-hook-form context provider */
export function TimeField({ name, rules, placeholder = 'Pick a time' }: TimeFieldProps) {
  const { control } = useFormContext();

  const hourValues = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const hourValue = i + 1;
      return hourValue.toString();
    });
  }, []);

  // Generate minutes 00-59 in 5-minute intervals for better UX

  const minuteValues = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const minuteValue = i * 5;
      return minuteValue.toString().padStart(2, '0');
    });
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        // Parse the time value if it exists
        const timeValue = field.value || null;

        return (
          <>
            <TimePicker
              value={timeValue}
              onChange={(time) => field.onChange(time)}
              placeholder={placeholder}
              hourValues={hourValues}
              minuteValues={minuteValues}
            />
            {fieldState.error && (
              <p className="text-sm font-medium text-destructive mt-1">{fieldState.error.message}</p>
            )}
          </>
        );
      }}
    />
  );
}

interface TimePickerParams {
  value: string;
  onChange: (time: string) => void;
  placeholder?: string;
  hourValues: string[];
  minuteValues: string[];
}
function TimePicker({ value, onChange, hourValues, minuteValues, placeholder = 'Select time' }: TimePickerParams) {
  console.log(value);
  const [selectedTime, setSelectedTime] = useState(value);
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState('AM');
  const [open, setOpen] = useState(false);

  // Generate hours 1-12
  const hours = hourValues;

  const minutes = minuteValues;

  const formatTime = (h: string, m: string, p: string) => {
    return `${h}:${m} ${p}`;
  };

  const handleTimeConfirm = () => {
    const timeString = formatTime(hour, minute, period);
    setSelectedTime(timeString);
    console.log({ timeString });
    onChange(timeString);
    setOpen(false);
  };

  return (
    <>
      <Popover defaultOpen={false} open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            data-empty={!selectedTime}
            role="button"
            tabIndex={0}
            className="data-[empty=true]:text-muted-foreground border text-base h-10 rounded-xl px-3 py-2 md:text-sm cursor-pointer flex items-center flex-row-reverse justify-between gap-3">
            <Clock className="h-3 w-3" />
            {selectedTime ? <span>{selectedTime}</span> : <span>{placeholder}</span>}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start" side="top">
          <div className="space-y-5">
            {/* Time Controls */}
            <div className="grid grid-cols-3 gap-2">
              {/* Hour Selector */}
              <div>
                <Select value={hour} onValueChange={setHour}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {hours.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Minute Selector */}
              <div>
                <Select value={minute} onValueChange={setMinute}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {minutes.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* AM/PM Toggle */}
              <div className="flex h-8">
                <Button
                  type="button"
                  variant={period === 'AM' ? 'default' : 'outline'}
                  className="rounded-r-none flex-1 text-xs h-8 px-1"
                  onClick={() => setPeriod('AM')}>
                  AM
                </Button>
                <Button
                  type="button"
                  variant={period === 'PM' ? 'default' : 'outline'}
                  className="rounded-l-none flex-1 text-xs h-8 px-1"
                  onClick={() => setPeriod('PM')}>
                  PM
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end">
              {/* <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  const now = new Date();
                  const currentHour = now.getHours();
                  const currentMinute = Math.round(now.getMinutes() / 5) * 5;

                  const hour12 = currentHour === 0 ? 12 : currentHour > 12 ? currentHour - 12 : currentHour;
                  const ampm = currentHour >= 12 ? 'PM' : 'AM';

                  setHour(hour12.toString());
                  setMinute(currentMinute.toString().padStart(2, '0'));
                  setPeriod(ampm);
                }}>
                Now
              </Button> */}
              <Button type="button" size="sm" className="flex-1" onClick={handleTimeConfirm}>
                Done
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
